import React, { useState, useEffect } from 'react';
import { Card, Form, Button, InputGroup, Spinner, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { 
  getGlobalExportSettings, 
  updateGlobalExportSettings,
  GlobalExportSettings 
} from '../../../services/professionService';
import { COLORS } from '../types/profession.types';

const GlobalExportSettingsTab: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [trancheSize, setTrancheSize] = useState<number>(1000);
  const [pricePerTranche, setPricePerTranche] = useState<number>(1.00);
  
  const [currentSettings, setCurrentSettings] = useState<GlobalExportSettings | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await getGlobalExportSettings();
      setCurrentSettings(settings);
      setTrancheSize(settings.tranche_size);
      setPricePerTranche(settings.price_per_tranche);
    } catch (error) {
      console.log("Erreur chargement, utilisation des valeurs par défaut");
      setCurrentSettings(null);
      setTrancheSize(1000);
      setPricePerTranche(1.00);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await updateGlobalExportSettings(trancheSize, pricePerTranche);
      setCurrentSettings(updated); 
      toast.success("Configuration mise à jour avec succès");
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de la sauvegarde");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const calculatePrice = (contacts: number) => {
    const tranches = Math.ceil(contacts / trancheSize);
    return tranches * pricePerTranche;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" style={{ color: COLORS.primary }} />
        <p className="mt-3 text-secondary">Chargement de la configuration...</p>
      </div>
    );
  }

  return (
    <div>
      <Card className="border-0 shadow-sm" style={{ borderRadius: "12px" }}>
        <Card.Body className="p-4">
          {/* En-tête */}
          <div className="mb-4">
            <h4 className="fw-semibold mb-2" style={{ color: COLORS.text }}>
              Configuration des exports
            </h4>
            <p style={{ color: COLORS.textLight, fontSize: "0.95rem", marginBottom: 0 }}>
              Définissez le prix par tranche de contacts. Le nombre de tranches est arrondi au supérieur.
            </p>
          </div>

          {/* Formulaire */}
          <Row className="g-4 mb-4">
            <Col xs={12} md={6}>
              <Form.Label className="fw-medium" style={{ color: COLORS.text }}>
                Nombre de contacts par tranche
              </Form.Label>
              <InputGroup style={{ maxWidth: "250px" }}>
                <Form.Control
                  type="number"
                  min="1"
                  step="100"
                  value={trancheSize}
                  onChange={(e) => setTrancheSize(Number(e.target.value))}
                  style={{ 
                    borderColor: COLORS.border,
                    fontSize: "16px",
                    height: "46px"
                  }}
                />
                <InputGroup.Text style={{ 
                  backgroundColor: COLORS.light, 
                  borderColor: COLORS.border,
                  color: COLORS.text
                }}>
                  contacts
                </InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-secondary">
                Exemple : 600, 1000 ou 2000 contacts par tranche
              </Form.Text>
            </Col>

            <Col xs={12} md={6}>
              <Form.Label className="fw-medium" style={{ color: COLORS.text }}>
                Prix par tranche
              </Form.Label>
              <InputGroup style={{ maxWidth: "250px" }}>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.5"
                  value={pricePerTranche}
                  onChange={(e) => setPricePerTranche(Number(e.target.value))}
                  style={{ 
                    borderColor: COLORS.border,
                    fontSize: "16px",
                    height: "46px"
                  }}
                />
                <InputGroup.Text style={{ 
                  backgroundColor: COLORS.light, 
                  borderColor: COLORS.border,
                  color: COLORS.text
                }}>
                  €
                </InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-secondary">
                Prix unitaire par tranche
              </Form.Text>
            </Col>
          </Row>

          {/* Note explicative */}
          <Alert 
            variant="info" 
            className="mb-4"
            style={{ 
              backgroundColor: '#f0f7ff', 
              borderColor: '#d0e0ff',
              borderRadius: '8px'
            }}
          >
            <div className="d-flex">
              <div className="flex-grow-1">
                <strong className="d-block mb-2">Règle de calcul :</strong>
                <code className="d-block p-2 bg-white rounded mb-2">
                  Nombre de tranches = ceil(contacts / {trancheSize}) × {pricePerTranche}€
                </code>
                <span className="text-secondary small">
                  Exemple : 5 600 contacts → 5600/{trancheSize} = {(5600/trancheSize).toFixed(2)} → {Math.ceil(5600/trancheSize)} tranches → {Math.ceil(5600/trancheSize) * pricePerTranche}€
                </span>
              </div>
            </div>
          </Alert>

          {/* Configuration actuelle */}
          {currentSettings && (
            <div className="mb-4 p-3 rounded bg-light">
              <div className="small text-secondary mb-2">Configuration en vigueur</div>
              <div className="d-flex gap-4">
                <div>
                  <span className="text-secondary">Tranche :</span>
                  <span className="ms-2 fw-semibold">{currentSettings.tranche_size} contacts</span>
                </div>
                <div>
                  <span className="text-secondary">Prix :</span>
                  <span className="ms-2 fw-semibold">{currentSettings.price_per_tranche} €</span>
                </div>
              </div>
              <div className="small text-secondary mt-2">
                Dernière mise à jour : {new Date(currentSettings.updated_at).toLocaleString('fr-FR')}
                {currentSettings.updatedByUser && ` par ${currentSettings.updatedByUser.firstname} ${currentSettings.updatedByUser.name}`}
              </div>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="d-flex justify-content-end gap-3">
            <Button
              variant="outline-secondary"
              onClick={loadSettings}
              disabled={saving}
              style={{
                borderColor: COLORS.border,
                color: COLORS.textLight,
                padding: "10px 24px",
                borderRadius: "8px",
                minWidth: "120px",
                backgroundColor: 'transparent'
              }}
            >
              Actualiser
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
                color: 'white',
                padding: "10px 24px",
                borderRadius: "8px",
                minWidth: "150px",
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default GlobalExportSettingsTab;